import { logger } from './logger';
import { stateManager } from './state-manager';
import { CONTAINER_ID, IFRAME_ID, getContainerStyles, getIframeStyles } from './styles';

/**
 * Iframe 생성 및 관리
 * - Lazy creation: open() 시에만 DOM 생성
 * - 컨테이너/iframe 라이프사이클 관리
 */
class AICXIframeManager {
  /** iframe 로드 실패 시 호출할 에러 콜백 */
  private onError: ((error: Error) => void) | null = null;
  /** 전체 화면 모달을 여는 동안 비활성화한 호스트 요소와 기존 상태 */
  private inertSiblings = new Map<HTMLElement, boolean>();

  /**
   * 에러 콜백 등록
   * AICX 클래스에서 eventEmitter.emit('error')와 연결
   */
  setOnError(callback: (error: Error) => void): void {
    this.onError = callback;
  }

  /**
   * iframe 표시
   * - 최초 호출 시 컨테이너/iframe 생성
   * - 이후 호출 시 display만 변경
   */
  show(): void {
    const config = stateManager.getState().config;

    if (!config || !config.chatAppUrl) {
      logger.error('설정이 없습니다. init()을 먼저 호출하세요.');
      return;
    }

    const iframeSrc = `${config.chatAppUrl.replace(/\/$/, '')}/${config.serviceKey}`;

    const container = this.getOrCreateContainer();
    const iframe = this.getOrCreateIframe(container, iframeSrc);

    container.style.display = 'block';
    if (this.isModal(config)) {
      this.disableModalBackground(container);
    } else {
      this.restoreModalBackground();
    }
    iframe.focus({ preventScroll: true });
    logger.log('iframe 표시됨');
  }

  /**
   * iframe 숨기기
   * - DOM은 유지하고 display만 변경
   */
  hide(): void {
    const { container } = stateManager.getState();

    if (container) {
      container.style.display = 'none';
      this.restoreModalBackground();
      logger.log('iframe 숨겨짐');
    }
  }

  /**
   * iframe 및 컨테이너 완전 제거
   */
  destroy(): void {
    const { container, iframe } = stateManager.getState();

    this.restoreModalBackground();

    // iframe 제거
    if (iframe?.parentNode) {
      iframe.parentNode.removeChild(iframe);
      logger.log('iframe 제거됨');
    }

    // 컨테이너 제거
    if (container?.parentNode) {
      container.parentNode.removeChild(container);
      logger.log('컨테이너 제거됨');
    }

    // 상태에서 참조 제거
    stateManager.setIframe(null);
    stateManager.setContainer(null);
    this.onError = null;
  }

  /**
   * 컨테이너를 가져오거나 없으면 생성
   */
  private getOrCreateContainer(): HTMLDivElement {
    const { container } = stateManager.getState();
    if (container) return container;

    const newContainer = this.createContainer();
    stateManager.setContainer(newContainer);
    return newContainer;
  }

  /**
   * iframe을 가져오거나 없으면 생성하여 컨테이너에 삽입
   */
  private getOrCreateIframe(container: HTMLDivElement, src: string): HTMLIFrameElement {
    const { iframe } = stateManager.getState();
    if (iframe) return iframe;

    const newIframe = this.createIframe(src);
    container.appendChild(newIframe);
    stateManager.setIframe(newIframe);
    return newIframe;
  }

  /**
   * 컨테이너 div 생성
   */
  private createContainer(): HTMLDivElement {
    const config = stateManager.getState().config!;
    const container = document.createElement('div');

    container.id = CONTAINER_ID;
    Object.assign(container.style, getContainerStyles(config));
    if (this.isModal(config)) {
      container.setAttribute('role', 'dialog');
      container.setAttribute('aria-modal', 'true');
      container.setAttribute('aria-label', 'AICX 상담');
    }

    // 초기 상태는 숨김 (show()에서 표시)
    container.style.display = 'none';

    this.appendToParent(container, config.containerId);

    logger.log('컨테이너 생성됨', { containerId: config.containerId || 'body' });
    return container;
  }

  /**
   * 부모 요소에 엘리먼트 삽입
   */
  private appendToParent(element: HTMLElement, containerId?: string): void {
    const parent = containerId ? document.getElementById(containerId) : document.body;

    if (!parent) {
      logger.warn(`containerId "${containerId}"를 찾을 수 없습니다. body에 추가합니다.`);
      document.body.appendChild(element);
    } else {
      parent.appendChild(element);
    }
  }

  /**
   * iframe 생성
   */
  private createIframe(src: string): HTMLIFrameElement {
    const config = stateManager.getState().config!;
    const iframe = document.createElement('iframe');

    iframe.id = IFRAME_ID;
    iframe.title = 'AICX 상담';
    iframe.setAttribute('referrerpolicy', 'origin');
    iframe.src = src;

    // 보안 속성
    iframe.setAttribute('allow', 'clipboard-write');
    iframe.setAttribute(
      'sandbox',
      'allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-downloads'
    );

    // 스타일 적용
    Object.assign(iframe.style, getIframeStyles(config));

    // iframe 로드 이벤트
    iframe.onload = () => logger.log('iframe 로드 완료');
    iframe.onerror = () => {
      const error = new Error('iframe 로드 실패: URL을 확인하세요.');
      logger.error('iframe 로드 실패');
      this.onError?.(error);
    };

    logger.log('iframe 생성됨', { src });
    return iframe;
  }

  private isModal(
    config: NonNullable<ReturnType<typeof stateManager.getState>['config']>
  ): boolean {
    return (config.displayMode ?? 'full') === 'full' && !config.containerId;
  }

  private disableModalBackground(container: HTMLElement): void {
    this.restoreModalBackground();
    for (const sibling of Array.from(document.body.children)) {
      if (!(sibling instanceof HTMLElement) || sibling === container) continue;
      this.inertSiblings.set(sibling, sibling.hasAttribute('inert'));
      sibling.setAttribute('inert', '');
    }
  }

  private restoreModalBackground(): void {
    for (const [element, wasInert] of this.inertSiblings) {
      if (!wasInert) element.removeAttribute('inert');
    }
    this.inertSiblings.clear();
  }
}

/** 싱글톤 인스턴스 */
export const iframeManager = new AICXIframeManager();
